"use client";

import { cn } from "@/lib/utils/utils";
import { useRouter, useSearchParams } from "next/navigation";
import React, {
  BaseSyntheticEvent,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { useActionFeedback, useTransitionContext } from "./context";
import { ActionButton, ActionButtonProps } from "./trigger";
import { Action } from ".";

import {
  FieldValues,
  FormProvider,
  useForm,
  UseFormProps,
} from "react-hook-form";
export function ActionForm<T extends FieldValues>({
  children,
  action,
  className,
  id,
  ref,
  ...props
}: {
  action: Action<T>;
  children: ReactNode;
  className?: string;
  id?: string;
  ref?: React.RefObject<{
    submit: (e?: BaseSyntheticEvent | undefined) => Promise<void>;
    element: HTMLFormElement | null;
  }>;
} & UseFormProps<T>) {
  const [state, startAction] = useTransitionContext(id);
  const form = useForm<T>(props);
  const formElement = useRef<HTMLFormElement>(null);
  const [, setFeedback] = useActionFeedback();
  const router = useRouter();
  const submit = form.handleSubmit((data: T) => {
    setFeedback(undefined);
    return startAction(async () => {
      const feedback = await action(data);
      if (!!feedback && "type" in feedback) {
        setFeedback(feedback);
      }
      if (!!feedback && "redirect" in feedback) {
        router.push(feedback.redirect);
      }
      if (!!feedback && "back" in feedback) {
        router.back();
      }
    });
  });

  useImperativeHandle(ref, () => ({
    submit,
    element: formElement.current,
    defaultValues: props.defaultValues,
  }));

  return (
    <FormProvider {...form}>
      <form
        ref={formElement}
        onSubmit={submit}
        className={cn(className, id ? `${id}-${state}` : state)}
        id={id}
      >
        {children}
      </form>
    </FormProvider>
  );
}

export function QueryForm<T extends FieldValues>({
  mode = "onChange",
  children,
  className,
  id,
  ...props
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  mode?: "onChange" | "onSubmit";
} & UseFormProps<T>) {
  const [state, startAction] = useTransitionContext(id);
  const form = useForm<T>(props);
  const router = useRouter();
  const params = useSearchParams();
  const submit = form.handleSubmit((data: T) => {
    return startAction(async () => {
      const query = new URLSearchParams(params);
      for (const key in data) {
        if (data[key] !== null && data[key] !== undefined && data[key] !== "") {
          query.set(key, data[key]);
        } else {
          query.delete(key);
        }
      }
      router.push(`?${query.toString()}`);
    });
  });

  useEffect(() => {
    if (mode === "onChange") {
      const { unsubscribe } = form.watch((data) => {
        startAction(async () => {
          const query = new URLSearchParams(params);
          for (const key in data) {
            if (
              data[key] !== null &&
              data[key] !== undefined &&
              data[key] !== ""
            ) {
              query.set(key, data[key]);
            } else {
              query.delete(key);
            }
          }
          router.push(`?${query.toString()}`);
        });
      });
      return () => unsubscribe();
    }
  }, [mode, params, form, router, state, startAction]);

  return (
    <FormProvider {...form}>
      <form
        id={id}
        onSubmit={submit}
        className={cn(className, id ? `${id}-${state}` : state)}
      >
        {children}
      </form>
    </FormProvider>
  );
}

export const ActionSubmit = React.forwardRef<
  HTMLButtonElement,
  Omit<ActionButtonProps, "pending">
>(function ActionSubmit(props, ref) {
  const [state] = useTransitionContext(props.form ?? props.id);
  return (
    <ActionButton
      {...props}
      ref={ref}
      type="submit"
      pending={state === "pending"}
    />
  );
});
